// Step 4 — Schedule. Channel-aware. Each channel gets its own card with an
// icon header. Email is "Send now" or "Schedule for later" (radio cards). The
// in-app window can be locked to the email send time via a prominent toggle.

import React, { useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Icon, Typo, Textinput } from '../../../../ui';
import { CHANNEL, LIMITS, OFFER } from '../../constants';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SectionCard = styled.section`
  background: ${({ theme }) => theme.colors.NEU_0};
  border: 1px solid ${({ theme }) => theme.colors.NEU_200};
  border-radius: 12px;
  display: flex;
  flex-direction: column;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 22px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.NEU_100};
  font-weight: 600;
  font-size: 15px;
`;

const SectionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px 22px 22px;
`;

const RadioCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px 18px;
  border-radius: 10px;
  border: 2px solid ${({ $selected, theme }) => ($selected ? theme.colors.PRIMARY : theme.colors.NEU_200)};
  background: ${({ $selected, theme }) => ($selected ? theme.colors.PRIMARY_LIGHT : theme.colors.NEU_0)};
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover { border-color: ${({ theme }) => theme.colors.PRIMARY}; }
`;

const RadioRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const RadioMark = styled.span`
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid ${({ $selected, theme }) => ($selected ? theme.colors.PRIMARY : theme.colors.NEU_300)};
  background: ${({ theme }) => theme.colors.NEU_0};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;

  &::after {
    content: '';
    display: ${({ $selected }) => ($selected ? 'block' : 'none')};
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.PRIMARY};
  }
`;

const RadioBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RadioTitle = styled.span`
  font-weight: 600;
`;

const RadioDescription = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.NEU_600};
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;

  & > * { flex: 1 1 200px; min-width: 0; }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;

  & > span:first-child {
    font-size: 13px;
    color: ${({ theme }) => theme.colors.NEU_600};
  }
`;

const TimezoneSelect = styled.select`
  font-family: inherit;
  font-size: 14px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.NEU_200};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.NEU_0};
`;

const SyncBanner = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 10px;
  background: ${({ $on, theme }) => ($on ? theme.colors.PRIMARY_LIGHT : theme.colors.NEU_50)};
  color: ${({ theme }) => theme.colors.NEU_PRIMARY};
  cursor: pointer;
  font-weight: 500;
  font-size: 13px;
`;

const SyncCheck = styled.input.attrs({ type: 'checkbox' })`
  width: 18px;
  height: 18px;
  accent-color: ${({ theme }) => theme.colors.PRIMARY};
  cursor: pointer;
`;

const SyncTime = styled.span`
  margin-left: auto;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.PRIMARY};
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.NEU_600};
`;

const Warning = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${({ theme }) => theme.colors.MARKER_LIGHT};
  color: ${({ theme }) => theme.colors.MARKER};
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
`;

const InfoBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  background: ${({ theme }) => theme.colors.NEU_50};
  color: ${({ theme }) => theme.colors.NEU_600};
  border: 1px solid ${({ theme }) => theme.colors.NEU_200};
  border-radius: 10px;
  font-size: 13px;
`;

const FieldError = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.ERROR_RED};
`;

const TIMEZONES = ['Europe/Stockholm', 'Europe/Lisbon', 'Europe/London', 'America/New_York', 'UTC'];

const isoToDateInput = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  } catch { return ''; }
};

const isoToTimeInput = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ''; }
};

const combineDateTime = (date, time) => {
  if (!date) return null;
  const t = time || '09:00';
  try { return new Date(`${date}T${t}`).toISOString(); } catch { return null; }
};

const formatShort = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

const ScheduleRadioCard = ({ selected, title, description, onSelect, children }) => (
  <RadioCard $selected={selected} onClick={onSelect} role='radio' aria-checked={selected} tabIndex={0}>
    <RadioRow>
      <RadioMark $selected={selected} />
      <RadioBody>
        <RadioTitle>{title}</RadioTitle>
        {description && <RadioDescription>{description}</RadioDescription>}
      </RadioBody>
    </RadioRow>
    {selected && children}
  </RadioCard>
);

const StepSchedule = ({ draft, errors = {}, onChange }) => {
  const { t } = useTranslation();
  const channels = draft?.channels ?? [];
  const offer = draft?.offer ?? OFFER.NONE;
  const bothChannels = channels.length === 2;
  const email = draft?.schedule?.email ?? {};
  const upsell = draft?.schedule?.networkUpsell ?? {};
  const syncStartTimes = draft?.schedule?.syncStartTimes ?? false;

  const updateSchedule = useCallback((patch) => {
    onChange({ ...draft, schedule: { ...(draft?.schedule ?? {}), ...patch } });
  }, [draft, onChange]);

  const updateEmail = useCallback((patch) => {
    updateSchedule({ email: { ...email, ...patch } });
  }, [email, updateSchedule]);

  const updateUpsell = useCallback((patch) => {
    const next = { ...upsell, ...patch };
    if (syncStartTimes && patch.activeFrom == null && email.sendAt) {
      next.activeFrom = email.sendAt;
    }
    updateSchedule({ networkUpsell: next });
  }, [email.sendAt, syncStartTimes, upsell, updateSchedule]);

  const setMode = (mode) => {
    if (mode === 'now') {
      updateEmail({ mode, sendAt: new Date().toISOString() });
    } else {
      updateEmail({ mode });
    }
  };

  const offerExpiry = offer === OFFER.DISCOUNT ? draft?.discount?.validUntil : draft?.invitation?.expiresAt;
  const upsellOverflowsOffer = offerExpiry && upsell.activeUntil && new Date(upsell.activeUntil).getTime() > new Date(offerExpiry).getTime();

  const effectiveActiveFrom = syncStartTimes && email.sendAt ? email.sendAt : upsell.activeFrom;

  const handleEmailDate = (e) => {
    const iso = combineDateTime(e.target.value, isoToTimeInput(email.sendAt));
    updateEmail({ sendAt: iso });
  };

  const handleEmailTime = (e) => {
    const iso = combineDateTime(isoToDateInput(email.sendAt) || isoToDateInput(new Date().toISOString()), e.target.value);
    updateEmail({ sendAt: iso });
  };

  return (
    <Stack>
      <Intro>
        <Typo.ContentSmallHeader>{t('campaigns.schedule.title', 'When should it go out?')}</Typo.ContentSmallHeader>
        <Typo.SmallText color='textSecondary'>
          {t(
            'campaigns.schedule.description',
            'Each channel has its own timing. The in-app window can start automatically when the email sends.',
          )}
        </Typo.SmallText>
      </Intro>

      {channels.includes(CHANNEL.EMAIL) && (
        <SectionCard>
          <SectionHeader>
            <Icon.Email size='small' />
            {t('campaigns.schedule.emailSend', 'Email send')}
          </SectionHeader>
          <SectionBody>
            <ScheduleRadioCard
              selected={email.mode === 'now'}
              title={t('campaigns.schedule.sendNow', 'Send now')}
              description={t('campaigns.schedule.sendNowDescription', 'Starts queuing immediately after you launch.')}
              onSelect={() => setMode('now')}
            />

            <ScheduleRadioCard
              selected={email.mode !== 'now'}
              title={t('campaigns.schedule.scheduleSend', 'Schedule for later')}
              description={t('campaigns.schedule.scheduleSendDescription', 'Pick a specific date and time.')}
              onSelect={() => setMode('scheduled')}
            >
              <Row>
                <Field>
                  <span>{t('campaigns.schedule.date', 'Date')}</span>
                  <Textinput
                    type='date'
                    value={isoToDateInput(email.sendAt)}
                    onChange={handleEmailDate}
                  />
                </Field>
                <Field>
                  <span>{t('campaigns.schedule.time', 'Time')}</span>
                  <Textinput
                    type='time'
                    value={isoToTimeInput(email.sendAt)}
                    onChange={handleEmailTime}
                  />
                </Field>
                <Field>
                  <span>{t('campaigns.schedule.timezone', 'Timezone')}</span>
                  <TimezoneSelect
                    value={email.timezone ?? 'Europe/Stockholm'}
                    onChange={(e) => updateEmail({ timezone: e.target.value })}
                  >
                    {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                  </TimezoneSelect>
                </Field>
              </Row>
              {errors.emailSendAt && <FieldError>{errors.emailSendAt}</FieldError>}
            </ScheduleRadioCard>
          </SectionBody>
        </SectionCard>
      )}

      {channels.includes(CHANNEL.NETWORK_UPSELL) && (
        <SectionCard>
          <SectionHeader>
            <Icon.TrendingUp size='small' />
            {t('campaigns.schedule.inAppWindow', 'In-app active window')}
          </SectionHeader>
          <SectionBody>
            {bothChannels && (
              <SyncBanner $on={syncStartTimes}>
                <SyncCheck
                  checked={syncStartTimes}
                  onChange={(e) => updateSchedule({ syncStartTimes: e.target.checked })}
                />
                {t('campaigns.schedule.syncStartTimes', 'Start in-app placement when the email sends')}
                {syncStartTimes && email.sendAt && <SyncTime>{formatShort(email.sendAt)}</SyncTime>}
              </SyncBanner>
            )}

            <Row>
              <Field>
                <span>{t('campaigns.schedule.activeFrom', 'Active from')}</span>
                <Textinput
                  type='date'
                  value={isoToDateInput(effectiveActiveFrom)}
                  disabled={syncStartTimes && bothChannels}
                  onChange={(e) => updateUpsell({ activeFrom: combineDateTime(e.target.value, '00:00') })}
                />
                {errors.upsellActiveFrom && <FieldError>{errors.upsellActiveFrom}</FieldError>}
              </Field>
              <Field>
                <span>{t('campaigns.schedule.activeUntil', 'Active until')}</span>
                <Textinput
                  type='date'
                  value={isoToDateInput(upsell.activeUntil)}
                  onChange={(e) => updateUpsell({ activeUntil: combineDateTime(e.target.value, '23:59') })}
                />
                {errors.upsellActiveUntil && <FieldError>{errors.upsellActiveUntil}</FieldError>}
              </Field>
              <Field>
                <span>{t('campaigns.schedule.timezone', 'Timezone')}</span>
                <TimezoneSelect
                  value={upsell.timezone ?? 'Europe/Stockholm'}
                  onChange={(e) => updateUpsell({ timezone: e.target.value })}
                >
                  {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                </TimezoneSelect>
              </Field>
            </Row>

            <Hint>
              {t(
                'campaigns.schedule.inAppHint',
                'Default window is {{days}} days.',
                { days: LIMITS.DEFAULT_UPSELL_WINDOW_DAYS },
              )}
              {offerExpiry && (
                <>
                  {' '}
                  {t('campaigns.schedule.offerExpiryHint', 'Offer expires {{date}} — you can extend the in-app window up to that date.', {
                    date: formatShort(offerExpiry),
                  })}
                </>
              )}
            </Hint>

            {upsellOverflowsOffer && (
              <Warning>
                <Icon.Info size='small' />
                {t('campaigns.schedule.overflowsOffer', 'In-app window extends past the offer expiry — students may see a card for an offer that has already expired.')}
              </Warning>
            )}
          </SectionBody>
        </SectionCard>
      )}

      <InfoBanner>
        <Icon.Info size='small' />
        <span>
          <strong>{t('campaigns.schedule.recurringTitle', 'Recurring sends')}</strong>{' '}
          {t('campaigns.schedule.recurringBody', 'are post-MVP. Currently a campaign fires once at the scheduled time.')}
        </span>
      </InfoBanner>
    </Stack>
  );
};

export default StepSchedule;
